import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Phone, Check, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from '@/integrations/supabase/client';
import { CountryPhoneSelector } from "@/components/booking/CountryPhoneSelector";

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, sendSupabaseOTP, verifySupabaseOTP, verifyCodeAndLogin, signInWithEmailPassword } = useAuth();
  const { toast } = useToast();
  
  
  // Load preferences from localStorage
  const loadPreferences = () => {
    try {
      const loginMethod = localStorage.getItem('login_method') as 'phone' | 'email' | null;
      const userTypePreference = localStorage.getItem('user_type_preference') as 'customer' | 'filmmaker' | null;
      return {
        loginMethod: loginMethod || 'email', // Default to 'email' instead of 'phone'
        userType: userTypePreference || 'customer',
      };
    } catch (error) {
      console.error('Error loading preferences:', error);
      return { loginMethod: 'email' as const, userType: 'customer' as const }; // Default to 'email'
    }
  };

  const { loginMethod: initialLoginMethod, userType: initialUserType } = loadPreferences();
  
  // TEMPORARY: Phone login is disabled - only email is available
  // TODO: Re-enable phone login in the future
  const [loginMethod, setLoginMethod] = useState<'phone' | 'email'>('email'); // Always use 'email' for now
  const [step, setStep] = useState<'phone' | 'code'>('phone');
  const [phonePrefix, setPhonePrefix] = useState('+1'); // Padrão: Canadá
  const [phone, setPhone] = useState(''); // Apenas o número (sem prefixo)
  const [fullPhone, setFullPhone] = useState('+1'); // Número completo para exibição
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [userType, setUserType] = useState<'customer' | 'filmmaker'>(initialUserType);
  const [devCode, setDevCode] = useState<string | null>(null);

  const returnUrl = searchParams.get('returnUrl') || (userType === 'filmmaker' ? '/filmmaker' : '/book/step-1');

  // Save preferences to localStorage
  const savePreferences = (method: 'phone' | 'email', type: 'customer' | 'filmmaker') => {
    try {
      localStorage.setItem('login_method', method);
      localStorage.setItem('user_type_preference', type);
    } catch (error) {
      console.error('Error saving preferences:', error);
    }
  };

  // Update login method and save preference
  const handleLoginMethodChange = (method: 'phone' | 'email') => {
    setLoginMethod(method);
    savePreferences(method, userType);
    // Reset form and step when switching methods
    setStep('phone'); // Always reset to phone step (which shows the login form)
    if (method === 'phone') {
      setEmail('');
      setPassword('');
    } else {
      setPhone('');
      setCode(['', '', '', '', '', '']);
      setFullPhone('+1');
    }
  };

  // Update user type and save preference
  const handleUserTypeChange = (type: 'customer' | 'filmmaker') => {
    setUserType(type);
    savePreferences(loginMethod, type);
  };

  // Shared function to verify filmmaker role
  const verifyFilmmakerRole = async (): Promise<{ success: boolean; error?: string }> => {
    try {
      // Wait for session to be fully established
      await new Promise(resolve => setTimeout(resolve, 500));

      const { data: authData, error: authError } = await supabase.auth.getUser();
      
      if (authError || !authData?.user?.id) {
        throw new Error(authError?.message || 'Not authenticated');
      }

      const userId = authData.user.id;
      // Checking filmmaker role

      // Verify filmmaker role with retry logic
      let roleData = null;
      let filmmakerRecord = null;
      let attempts = 0;
      const maxAttempts = 3;

      while (attempts < maxAttempts && !roleData) {
        const { data, error } = await supabase
          .from('user_roles')
          .select('role, location_id')
          .eq('user_id', userId)
          .eq('role', 'filmmaker')
          .maybeSingle();

        console.log(`Filmmaker role check attempt ${attempts + 1}:`, { data, error });

        if (data) {
          roleData = data;
          break;
        }

        if (error) {
          console.error('Role check error:', error);
        }

        attempts++;
        if (attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      }

      // Fallback: Check if user exists in filmmakers table (if RLS allows)
      if (!roleData) {
        console.log('Role not found in user_roles, checking filmmakers table...');
        const { data: filmmakerData, error: filmmakerError } = await supabase
          .from('filmmakers')
          .select('id, location_id')
          .eq('id', userId)
          .maybeSingle();

        console.log('Filmmaker table check:', { filmmakerData, filmmakerError });

        if (filmmakerData) {
          filmmakerRecord = filmmakerData;
          console.warn('⚠️ User found in filmmakers table but missing role in user_roles');
        }
      }

      // Block login if user doesn't have filmmaker role
      if (!roleData && !filmmakerRecord) {
        console.error('Filmmaker role not found after', attempts, 'attempts for user:', userId);
        
        // Sign out the user since they shouldn't be logged in
        await supabase.auth.signOut();
        
        return {
          success: false,
          error: 'Your account is not a filmmaker. Please contact support to upgrade your account.'
        };
      }

      // If user is in filmmakers table but missing role, still allow login but warn
      if (!roleData && filmmakerRecord) {
        console.warn('⚠️ User logged in but missing filmmaker role in user_roles table');
        toast({
          title: 'Warning',
          description: 'Your filmmaker role needs to be updated. Please contact support.',
          variant: 'default',
        });
      }

      console.log('✅ Filmmaker role verified successfully');
      return { success: true };
    } catch (e: any) {
      console.error('Filmmaker role check failed:', e);
      
      // Sign out on error to prevent partial login
      await supabase.auth.signOut();
      
      return {
        success: false,
        error: e.message || 'Could not verify filmmaker role. Please try again or contact support.'
      };
    }
  };

  const formatPhoneNumber = (value: string, prefix: string = phonePrefix): string => {
    // Remove todos os caracteres não numéricos
    const numbers = value.replace(/\D/g, '');
    
    // Se o prefixo é +55, aplica formatação brasileira: 11 97665-6681
    if (prefix.startsWith('+55')) {
      // Limita a 11 dígitos (DDD 2 + número 9)
      const limitedNumbers = numbers.slice(0, 11);
      
      // Aplica a formatação brasileira: 11 97665-6681
      if (limitedNumbers.length === 0) {
        return '';
      } else if (limitedNumbers.length <= 2) {
        return limitedNumbers;
      } else if (limitedNumbers.length <= 7) {
        return `${limitedNumbers.slice(0, 2)} ${limitedNumbers.slice(2)}`;
      } else {
        return `${limitedNumbers.slice(0, 2)} ${limitedNumbers.slice(2, 7)}-${limitedNumbers.slice(7)}`;
      }
    }
    
    // Para +1 ou outros, aplica formatação canadense: (123) 456-7890
    // Limita a 10 dígitos
    const limitedNumbers = numbers.slice(0, 10);
    
    // Aplica a formatação canadense: (123) 456-7890
    if (limitedNumbers.length === 0) {
      return '';
    } else if (limitedNumbers.length <= 3) {
      return `(${limitedNumbers}`;
    } else if (limitedNumbers.length <= 6) {
      return `(${limitedNumbers.slice(0, 3)}) ${limitedNumbers.slice(3)}`;
    } else {
      return `(${limitedNumbers.slice(0, 3)}) ${limitedNumbers.slice(3, 6)}-${limitedNumbers.slice(6)}`;
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast({
        title: "Invalid input",
        description: "Please enter both email and password",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    
    try {
      console.log('🔐 Signing in with email:', email);
      const result = await signInWithEmailPassword(email, password);
      
      if (!result.success) {
        toast({
          title: "Sign in failed",
          description: result.error || "Invalid email or password",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }
      
      // Sign in successful!
      console.log('✅ Login successful!');

      // Save preferences after successful login
      savePreferences(loginMethod, userType);

      // If filmmaker mode, verify filmmaker role in user_roles before redirect
      if (userType === 'filmmaker') {
        const roleCheck = await verifyFilmmakerRole();
        
        if (!roleCheck.success) {
          toast({
            title: 'Access denied',
            description: roleCheck.error || 'Your account is not a filmmaker. Please contact support to upgrade your account.',
            variant: 'destructive',
          });
          
          // Reset form
          setEmail('');
          setPassword('');
          setLoading(false);
          return;
        }

        toast({
          title: 'Welcome back!',
          description: 'Successfully logged in as filmmaker',
        });
        navigate('/filmmaker');
      } else {
        toast({
          title: 'Welcome back!',
          description: "You've successfully logged in",
        });
        navigate('/book/step-1');
      }
    } catch (error: any) {
      console.error('Error signing in:', error);
      toast({
        title: "Sign in error",
        description: error.message || "Please try again",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Verifica o número correto de dígitos baseado no prefixo
    const phoneDigits = phone.replace(/\D/g, '');
    let requiredDigits = 10; // padrão para +1
    let errorMessage = "Please enter a valid phone number";
    
    if (phonePrefix.startsWith('+55')) {
      // +55 precisa de 11 dígitos (DDD 2 + número 9)
      requiredDigits = 11;
      errorMessage = "Phone number must be 11 digits (DDD + number)";
    } else if (phonePrefix.startsWith('+1')) {
      // +1 precisa de 10 dígitos
      requiredDigits = 10;
      errorMessage = "Phone number must be 10 digits";
    }
    
    if (phoneDigits.length < requiredDigits) {
      toast({
        title: "Invalid phone number",
        description: errorMessage,
        variant: "destructive",
      });
      return;
    }

    // Combina prefixo + número para criar o número completo
    const normalizedPhone = phonePrefix + phoneDigits;
    const formattedFullPhone = phonePrefix + ' ' + phone;
    setFullPhone(formattedFullPhone);

    // Phone submit

    setLoading(true);
    
    try {
      // Always use Supabase Phone OTP (native method)
      console.log('📱 Sending Supabase Phone OTP');
      const result = await sendSupabaseOTP(normalizedPhone);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to send SMS');
      }
      
      // Check if in dev mode (Vonage failed but code was generated) or mock mode
      if (result.devCode) {
        console.log('🔐 DEV CODE:', result.devCode);
        setDevCode(result.devCode);
        toast({
          title: "Code sent!",
          description: `DEV MODE: Your code is ${result.devCode}`,
        });
      } else {
        toast({
          title: "Code sent!",
          description: "Check your phone for the verification code",
        });
      }
      
      setStep('code');
    } catch (error: any) {
      console.error('Error sending SMS:', error);
      toast({
        title: "Error sending SMS",
        description: error.message || "Please try again",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (index: number, value: string) => {
    if (value.length > 1) {
      value = value[0];
    }
    
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`code-${index + 1}`);
      nextInput?.focus();
    }

    // Auto-submit when all digits entered
    if (newCode.every(digit => digit) && index === 5) {
      handleCodeSubmit(newCode);
    }
  };

  const handleCodeSubmit = async (codeArray: string[] = code) => {
    const enteredCode = codeArray.join('');
    
    if (enteredCode.length !== 6) {
      toast({
        title: "Invalid code",
        description: "Please enter all 6 digits",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      // Use Supabase Phone OTP (native method)
        console.log('🔍 Verifying Supabase Phone OTP');
        const normalizedPhoneForAuth = phonePrefix + phone.replace(/\D/g, '');
        const result = await verifySupabaseOTP(normalizedPhoneForAuth, enteredCode);
        
        if (!result.success) {
          setLoading(false);
          toast({
            title: "Invalid code",
            description: result.error || "Please check your code and try again",
            variant: "destructive",
          });
          setCode(['', '', '', '', '', '']);
          return;
        }
        
        // SMS verified successfully!
        console.log('✅ Login successful!');

        // Save preferences after successful login
        savePreferences(loginMethod, userType);

        // If filmmaker mode, verify filmmaker role in user_roles before redirect
        if (userType === 'filmmaker') {
          const roleCheck = await verifyFilmmakerRole();
          
          if (!roleCheck.success) {
            toast({
              title: 'Access denied',
              description: roleCheck.error || 'Your account is not a filmmaker. Please contact support to upgrade your account.',
              variant: 'destructive',
            });
            
            // Reset form and go back to login
            setCode(['', '', '', '', '', '']);
            setStep('phone');
            setLoading(false);
            return;
          }

          toast({
            title: 'Welcome back!',
            description: 'Successfully logged in as filmmaker',
          });
          navigate('/filmmaker');
        } else {
          toast({
            title: 'Welcome back!',
            description: "You've successfully logged in",
          });
          navigate('/book/step-1');
        }
    } catch (error: any) {
      console.error('Error verifying code:', error);
      toast({
        title: "Verification error",
        description: error.message || "Please try again",
        variant: "destructive",
      });
      setCode(['', '', '', '', '', '']);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      const prevInput = document.getElementById(`code-${index - 1}`);
      prevInput?.focus();
    }
  };

  // Handler for back button
  const handleBackClick = () => {
    if (step === 'code') {
      // If on code step, go back to phone step
      setStep('phone');
    } else {
      // Normal back navigation - go to previous page in history
      navigate(-1);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="sticky top-0 z-10 bg-background border-b border-border px-4 py-3 flex items-center gap-3">
        <button
          onClick={handleBackClick}
          className="p-2 -m-2 hover:bg-muted rounded-lg transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="text-lg font-semibold">Sign In</h1>
      </header>

      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md space-y-6">
          {step === 'phone' ? (
            <>
              <div className="text-center space-y-2">
                <div className="flex justify-center mb-4">
                  <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                    {loginMethod === 'phone' ? (
                      <Phone className="h-8 w-8 text-primary" />
                    ) : (
                      <Mail className="h-8 w-8 text-primary" />
                    )}
                  </div>
                </div>
                <h2 className="text-2xl font-bold">Sign In</h2>
                <p className="text-muted-foreground">
                  Choose your account type and enter your email and password
                </p>
              </div>

              {/* TEMPORARY: Login Method Toggle is disabled - only email is available */}
              {/* TODO: Re-enable phone/email toggle in the future */}
              {/* 
              <div className="relative flex items-center p-1.5 bg-muted/30 rounded-xl border border-border/50">
                <div
                  className={`absolute top-1.5 bottom-1.5 w-[calc(50%-6px)] rounded-lg bg-background shadow-md border border-border/50 transition-all duration-300 ease-out ${
                    loginMethod === 'phone' 
                      ? 'left-1.5' 
                      : 'left-[calc(50%+4.5px)]'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => handleLoginMethodChange('phone')}
                  className={`relative z-10 flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg text-sm font-semibold transition-colors duration-200 ${
                    loginMethod === 'phone'
                      ? 'text-primary'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Phone className={`h-4 w-4 transition-colors duration-200 ${
                    loginMethod === 'phone' ? 'text-primary' : 'text-muted-foreground'
                  }`} />
                  <span>Phone</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleLoginMethodChange('email')}
                  className={`relative z-10 flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg text-sm font-semibold transition-colors duration-200 ${
                    loginMethod === 'email'
                      ? 'text-primary'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Mail className={`h-4 w-4 transition-colors duration-200 ${
                    loginMethod === 'email' ? 'text-primary' : 'text-muted-foreground'
                  }`} />
                  <span>Email</span>
                </button>
              </div>
              */}

              {/* TEMPORARY: Phone login form is disabled - only email is available */}
              {/* TODO: Re-enable phone login form in the future */}
              {false && loginMethod === 'phone' ? (
                <form onSubmit={handlePhoneSubmit} className="space-y-4">
                  <div>
                    <div className="flex gap-2">
                      <CountryPhoneSelector
                        value={phonePrefix}
                        onChange={(newPrefix, newCountryCode) => {
                          // Reformata o telefone quando o prefixo muda
                          // Nota: countryCode é opcional e já é salvo no localStorage pelo componente
                          const currentPhoneDigits = phone.replace(/\D/g, '');
                          const reformatted = formatPhoneNumber(currentPhoneDigits, newPrefix);
                          setPhonePrefix(newPrefix);
                          setPhone(reformatted);
                        }}
                        className="h-14"
                      />
                      <Input
                        id="phone"
                        type="tel"
                        placeholder={phonePrefix.startsWith('+55') ? "11 97665-6681" : "(123) 456-7890"}
                        value={phone}
                        onChange={(e) => {
                          const formatted = formatPhoneNumber(e.target.value, phonePrefix);
                          setPhone(formatted);
                        }}
                        className="flex-1 h-14 text-lg"
                        autoFocus
                      />
                    </div>
                  </div>
                  
                  <Button
                    type="submit"
                    className="w-full h-14 text-base font-semibold rounded-xl"
                    disabled={loading || (() => {
                      const phoneDigits = phone.replace(/\D/g, '').length;
                      if (phonePrefix.startsWith('+55')) return phoneDigits < 11; // 11 dígitos para Brasil
                      if (phonePrefix.startsWith('+1')) return phoneDigits < 10; // 10 dígitos para Canadá/USA
                      return phoneDigits < 10; // padrão
                    })()}
                  >
                    {loading ? "Sending..." : "Send Code"}
                  </Button>

                  <div className="flex items-center justify-center gap-2 pt-2">
                    <Checkbox
                      id="filmmaker-mode"
                      checked={userType === 'filmmaker'}
                      onCheckedChange={(checked) => handleUserTypeChange(checked ? 'filmmaker' : 'customer')}
                      className="h-4 w-4"
                    />
                    <Label 
                      htmlFor="filmmaker-mode" 
                      className="text-sm text-muted-foreground cursor-pointer font-normal"
                    >
                      I am a filmmaker
                    </Label>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleEmailSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="h-14 text-lg"
                      autoFocus
                      required
                    />
                  </div>
                  
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label htmlFor="password">Password</Label>
                      <button
                        type="button"
                        onClick={() => navigate('/forgot-password')}
                        className="text-sm text-primary hover:text-primary/80 font-medium transition-colors"
                      >
                        Forgot password?
                      </button>
                    </div>
                    <Input
                      id="password"
                      type="password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="h-14 text-lg"
                      required
                    />
                  </div>
                  
                  <Button
                    type="submit"
                    className="w-full h-14 text-base font-semibold rounded-xl"
                    disabled={loading || !email || !password}
                  >
                    {loading ? "Signing in..." : "Sign In"}
                  </Button>

                  <div className="flex items-center justify-center gap-2 pt-2">
                    <Checkbox
                      id="filmmaker-mode-email"
                      checked={userType === 'filmmaker'}
                      onCheckedChange={(checked) => handleUserTypeChange(checked ? 'filmmaker' : 'customer')}
                      className="h-4 w-4"
                    />
                    <Label 
                      htmlFor="filmmaker-mode-email" 
                      className="text-sm text-muted-foreground cursor-pointer font-normal"
                    >
                      I am a filmmaker
                    </Label>
                  </div>
                </form>
              )}
            </>
          ) : (
            <>
              <div className="text-center space-y-2">
                <div className="flex justify-center mb-4">
                  <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                    <Check className="h-8 w-8 text-primary" />
                  </div>
                </div>
                 <h2 className="text-2xl font-bold">Enter verification code</h2>
                 <p className="text-muted-foreground">
                   We sent a 6-digit code to {fullPhone || `${phonePrefix} ${phone}`}
                 </p>
                 {devCode && (
                   <div className="mt-4 p-4 bg-yellow-50 border-2 border-yellow-400 rounded-lg">
                     <p className="text-sm font-semibold text-yellow-800 mb-1">🔐 DEV MODE - Verification Code:</p>
                     <p className="text-3xl font-bold text-yellow-900 tracking-wider">{devCode}</p>
                     <p className="text-xs text-yellow-700 mt-2">This code appears only in development</p>
                   </div>
                 )}
               </div>

              <div className="space-y-6">
                <div className="flex gap-2 justify-center">
                  {code.map((digit, index) => (
                    <Input
                      key={index}
                      id={`code-${index}`}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleCodeChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      className="h-14 w-12 text-center text-xl font-semibold"
                      disabled={loading}
                    />
                  ))}
                </div>

                <Button
                  onClick={() => handleCodeSubmit()}
                  className="w-full h-14 text-base font-semibold rounded-xl"
                  disabled={loading || code.some(d => !d)}
                >
                  {loading ? "Verifying..." : "Verify Code"}
                </Button>

                <button
                  onClick={() => {
                    setStep('phone');
                    setCode(['', '', '', '', '', '']);
                  }}
                  className="w-full text-sm text-primary font-medium"
                  disabled={loading}
                >
                  Resend code
                </button>
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
}